import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from 'react-bootstrap';
import { Lightbox } from '../../../../src/web/components';
import ImageCarouselAsset from './ImageCarouselAsset';
import { pushRoute } from '../../../../modules/routing';
import styles from './ImageCarousel.scss';
import Translation from '../../stateless/Translation';
import useMedia from '../../../../modules/react/hooks/useMedia';
import { resolveContentUrl } from '../../../../modules/assets';

const propTypes = {
  autoplay: PropTypes.bool,
  bannerText: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    file: PropTypes.string,
    filename: PropTypes.string,
    staticImg: PropTypes.bool,
    alt: PropTypes.string,
    link: PropTypes.string,
    target: PropTypes.string,
  })),
  imageClassname: PropTypes.string,
  showBanner: PropTypes.bool,
  disableLightBox: PropTypes.bool,
  sizeGuide: PropTypes.bool,
};

const defaultProps = {
  autoplay: undefined,
  bannerText: undefined,
  images: [],
  imageClassname: undefined,
  showBanner: undefined,
  disableLightBox: undefined,
  sizeGuide: undefined,
};

const onClickImage = img => (e) => {
  e.stopPropagation();
  const { link, target } = img;
  if (!link) {
    return null;
  }
  if (target === '_blank') {
    return window.open(link);
  }
  return pushRoute(link);
};

const ImageCarousel = ({
  images,
  autoplay,
  bannerText,
  imageClassname,
  showBanner,
  disableLightBox,
  sizeGuide,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);


  let filteredImages = images.filter(Boolean);
  if (!filteredImages.length) {
    filteredImages = [{ file: '/img/grid-4.jpg', alt: 'facility-image' }];
  }

  const banner = (
    <>
      {showBanner && bannerText && (
        <div className={styles.imageBanner}>
          <div className={`${styles.wrapper} color-secondary-background`}>
            <p>{bannerText}</p>
          </div>
        </div>
      )
      }
    </>
  );

  const lightboxItems = () => {
    if (
      lightboxIndex === null
      || lightboxIndex < 0
      || lightboxIndex >= filteredImages.length
      || disableLightBox
    ) {
      return false;
    }

    const items = filteredImages.map((img, index) => (
      <ImageCarouselAsset
        lightbox
        asset={img}
        key={img.link}
        index={index}
        onClick={() => onClickImage(img)}
        mediaClassnames={`${styles.lightboxItem} ${styles.image} ${img.link ? styles.pointer : ''}`}
      />
    ));

    const nextIndex = (lightboxIndex + 1) % items.length;
    const prevIndex = (lightboxIndex + (items.length - 1)) % items.length;

    const lightboxProps = {
      items,
      itemIndex: lightboxIndex,
      onClickClose: () => setLightboxIndex(null),
      onClickDot: setLightboxIndex,
      onClickNext: () => setLightboxIndex(nextIndex),
      onClickPrev: () => setLightboxIndex(prevIndex),
    };

    return <Lightbox {...lightboxProps} />;
  };
  const numberOfImages = filteredImages.length - 1;
  const carouselItems = filteredImages.map((asset, idx) => (
    <Carousel.Item key={asset.file || asset.mediumId} className="pb-no-click">
      <div
        className={`${styles.image} ${imageClassname}`}
      >
        <ImageCarouselAsset
          asset={asset}
          key={asset.file}
          index={idx}
          onClick={() => setLightboxIndex(idx)}
          mediaClassnames={`${styles.image} ${sizeGuide ? styles.guideImage : ''}`}
        />
      </div>
    </Carousel.Item>
  ));
  const isImageAmount3or4 = images.length >= 3 && images.length <= 4;
  const maxImagesToShow = images.length >= 5 ? 5 : Math.min(images.length, 3);
  const displayedImages = images.slice(0, maxImagesToShow);
  const showMorePhotosText = (idx, totalImages) => (idx === 4 && totalImages > 4)
    || (isImageAmount3or4 && idx === 2);

  const imageGallery = (
    <div className={`${styles.imageGalleryContainer} ${isImageAmount3or4 ? styles.twoImages : ''}`}>
      {displayedImages.map((image, idx) => (
        <figure key={image.id} className="relative">
          <button
            onClick={() => setLightboxIndex(idx)}
            type="button"
            aria-label={showMorePhotosText ? (
              <Translation id="enterprise.widgets.nap.photos.ariaAll" defaultMessage="View all photos" />
            ) : (
              <Translation id="enterprise.widgets.nap.photos.ariaOne" defaultMessage={`View image ${idx}`} />
            )}
          >
            {showMorePhotosText(idx, images.length) && (
              <div className={styles.moreTextContainer}>
                <div className={styles.moreText}>
                  {numberOfImages}+ <Translation id="enterprise.widgets.nap.photos" defaultMessage="Photos" />
                </div>
              </div>
            )}
            <img
              src={resolveContentUrl(image.file)}
              alt={`${image.alt}`}
              height={image.height}
              width={image.width}
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
          </button>
        </figure>
      ))
      }
    </div>
  );

  const deviceType = useMedia(
    ['(max-width: 449.99px)', '(min-width: 450px)'],
    ['mobile', 'desktop'],
    'default',
  );

  const isMobile = deviceType === 'mobile';

  return (
    <>
      {isMobile ? (
        <div>
          {filteredImages.length === 1 ? carouselItems : (
            <Carousel
              interval={autoplay ? 5000 : null}
              indicators={false}
              controls={images.length > 1}
              prevIcon={<span className="icon icon-back-arrow" />}
              nextIcon={<span className="icon icon-select-arrow" />}
              className={styles.root}
            >
              {carouselItems}
            </Carousel>
          )}
        </div>
      ) : (
        <section>
          <h2 className="sr-only"><Translation id="enterprise.widgets.nap.photos.galleryTitle" defaultMessage="Photo gallery" /></h2>
          {imageGallery}
        </section>
      )}
      {banner}
      {lightboxItems()}
    </>
  );
};

ImageCarousel.propTypes = propTypes;
ImageCarousel.defaultProps = defaultProps;

export default ImageCarousel;
